import * as React from "react";
import Box, { BoxProps } from "@mui/joy/Box";
import Sheet from "@mui/joy/Sheet";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import { IconButton, Stack, Typography } from "@mui/joy";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import AIChat from "./AIChatComponent";
import { accessTokenManager } from "../models/AccessTokenManager";
import AIChatToggle, { AIIcon } from "./header/AIChatToggle";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useTranslation } from "../i18n";

function Root(props: BoxProps) {
  return (
    <Box
      {...props}
      sx={[
        {
          // bgcolor: "#16161b",
          // backgroundImage: "url(%PUBLIC_URL%/images/background.png)",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top right",
          backgroundAttachment: "fixed",
          display: "grid",
          gridTemplateColumns: {
            // xs: "1fr",
            xs: "minmax(48px, 48px) minmax(150px, 1fr)",
            sm: "minmax(48px, 48px) minmax(450px, 1fr)",
            lg: "minmax(160px, 250px) minmax(500px, 1fr)",
          },
          gridTemplateRows: "1fr",
          minHeight: "100dvh",
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );
}

function Root1Col(props: BoxProps) {
  return (
    <Box
      {...props}
      sx={[
        {
          // bgcolor: "background.appBody",
          display: "grid",
          gridTemplateColumns: {
            // xs: "1fr",
            xs: "minmax(450px, 1fr)",
            sm: "minmax(450px, 1fr)",
            md: "minmax(500px, 1fr)",
          },
          gridTemplateRows: "64px 1fr",
          minHeight: "100dvh",
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );
}

function Header(props: BoxProps) {
  return (
    <Box
      component="header"
      className="Header"
      {...props}
      sx={[
        {
          p: 2,
          gap: 2,
          // bgcolor: "background.surface",
          backdropFilter: "blur(10px)",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          gridColumn: "1 / -1",
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );
}

function SideNav(props: BoxProps) {
  return (
    <Box
      component="nav"
      className="Navigation"
      {...props}
      sx={[
        {
          p: 0,
          // bgcolor: "background.surface",
          boxShadow: "1px 0px 2px #000000",
          background:
            "radial-gradient(circle at 50% 50%, rgb(0 0 0) 20%, transparent 80%), conic-gradient(from 45deg at 50% 50%, rgb(5 2 33) 0%, rgb(11 17 43) 25%, rgb(2 6 33) 50%, rgb(13 41 51) 75%, rgb(7 2 33) 100%)",
          borderTopRightRadius: "12px",
          borderBottomRightRadius: "12px",
          borderRight: "1px solid",
          borderColor: "divider",
          zIndex: 1,
          marginRight: -1,
          display: {
            xs: "initial",
            sm: "initial",
          },
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );
}

function SidePane(props: BoxProps) {
  return (
    <Box
      className="Inbox"
      {...props}
      sx={[
        {
          // bgcolor: "background.surface",
          display: {
            xs: "none",
            md: "initial",
          },
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );
}

const lskey = "ai-recent-conversation-id";
let recentConversationId: string | null = localStorage.getItem(lskey);
let panelDefaultSize = 0;

function Main(props: BoxProps) {
  const t = useTranslation();
  const [isCollapsed, setIsCollapsed] = React.useState(panelDefaultSize === 0);
  const ref = React.useRef<ImperativePanelHandle>(null);

  const [conversationId, setConversationId] = useState<string | null>(
    recentConversationId
  );

  const createNewConversation = React.useCallback(() => {
    const newConversationId = uuidv4();
    setConversationId(newConversationId);
    recentConversationId = newConversationId;
    localStorage.setItem(lskey, recentConversationId);
  }, []);

  useEffect(() => {
    if (!conversationId) {
      createNewConversation();
    }
  }, [conversationId, createNewConversation]);

  const accessToken = accessTokenManager.retrieve();

  const collapsePanel = React.useCallback(() => {
    const panel = ref.current;
    if (panel) {
      panel.collapse();
      // recentConversationId = null;
      // localStorage.removeItem(lskey);
    }
  }, [ref]);

  const expandPanel = () => {
    const panel = ref.current;
    if (panel) {
      panel.expand(50);
    }
  };

  return (
    <Box
      component="main"
      className="Main"
      {...props}
      sx={[
        {
          p: 0,
          margin: 0,
          maxHeight: "100dvh",
          overflow: "auto",
          // bgcolor: "background.surface",
          // borderLeft: "1px solid",
          // borderColor: "divider",
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      <PanelGroup direction="horizontal">
        <Panel defaultSize={100} style={{ overflow: "auto" }}>
          {props.children}
        </Panel>
        <PanelResizeHandle>
          <Box
            sx={{
              width: "4px",
              height: "100%",
              bgcolor: "divider",
              cursor: "col-resize",
              "&:hover": { bgcolor: "neutral.outlinedHoverBg" },
            }}
          />
        </PanelResizeHandle>
        {isCollapsed && (
          <Box p={1}>
            <AIChatToggle onClick={() => expandPanel()} />
          </Box>
        )}

        <Panel
          style={{ backgroundColor: "black" }}
          collapsible
          onCollapse={() => setIsCollapsed(true)}
          onExpand={() => setIsCollapsed(false)}
          defaultSize={panelDefaultSize}
          collapsedSize={0}
          minSize={20}
          maxSize={70}
          onResize={(size, prevSize) => {
            panelDefaultSize = size;
          }}
          ref={ref}
        >
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Stack
              direction="row"
              sx={{
                width: "100%",
                height: "40px",
                borderBottom: "1px solid #333",
              }}
            >
              <Typography
                p={1}
                sx={{ flex: 1 }}
                startDecorator={<AIIcon />}
                endDecorator={
                  <IconButton variant="plain" onClick={createNewConversation}>
                    <RestartAltIcon />{" "}
                    <Typography fontSize={10}>{t("Reset")}</Typography>
                  </IconButton>
                }
              >
                {t("AI Assistant")}
              </Typography>
              <IconButton variant="plain" onClick={collapsePanel}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Box
              sx={{
                height: "calc(100vh - 40px)",
                display: "flex",
                flexDirection: "column",
                fontFamily: "monospace",
              }}
            >
              {conversationId && !isCollapsed && (
                <AIChat
                  conversationId={conversationId}
                  accessToken={accessToken}
                />
              )}
            </Box>
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

function SideDrawer(
  props: BoxProps & { onClose: React.MouseEventHandler<HTMLDivElement> }
) {
  const { onClose, ...other } = props;
  return (
    <Box
      {...other}
      sx={[
        { position: "fixed", zIndex: 1200, width: "100%", height: "100%" },
        ...(Array.isArray(other.sx) ? other.sx : [other.sx]),
      ]}
    >
      <Box
        role="button"
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: (theme) =>
            `rgba(${theme.vars.palette.neutral.darkChannel} / 0.8)`,
        }}
      />
      <Sheet
        sx={{
          minWidth: 256,
          width: "max-content",
          height: "100%",
          p: 2,
          boxShadow: "lg",
          // bgcolor: "background.surface",
        }}
      >
        {other.children}
      </Sheet>
    </Box>
  );
}

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  Root,
  Header,
  SideNav,
  SidePane,
  SideDrawer,
  Main,
  Root1Col,
};
